import {
  Button,
  MessageBar,
  MessageBarBody,
  Text,
} from '@fluentui/react-components';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import {
  DataHubClientProvider,
  useDataHubClient,
} from '../../context/DataHubClientContext.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import type { AccessControlGroup } from '../../services/accessControlService.interface.js';
import { AccessControlService } from '../../services/accessControlService.js';
import { DataHubService } from '../../services/dataHubService.js';
import { WorkspaceService } from '../../services/workspaceService.js';
import type { Workspace } from '../../types/workspace.js';

import {
  convertWorkspaceToPrivate,
  isWorkspaceCreator,
} from './privateWorkspace.js';
import { WorkspaceRoleEditor } from './WorkspaceRoleEditor.js';

export interface PrivateWorkspaceConversionSectionProps {
  workspace: Workspace;
  onConverted: (workspace: Workspace) => void;
}

function PrivateWorkspaceConversionFields({
  workspace,
  onConverted,
}: PrivateWorkspaceConversionSectionProps) {
  const workspaceClient = useHttpClient();
  const dataHubClient = useDataHubClient();
  const { account } = useAuth();
  const callerIdentifiers = [account?.username, account?.localAccountId];
  const isCreator = isWorkspaceCreator(workspace, callerIdentifiers);
  const [ownerGroups, setOwnerGroups] = useState<AccessControlGroup[]>([]);
  const [contributorGroups, setContributorGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [readerGroups, setReaderGroups] = useState<AccessControlGroup[]>([]);
  const dependencies = useMemo(
    () => ({
      workspaceService: new WorkspaceService(workspaceClient),
      dataHubService: new DataHubService(dataHubClient),
      accessControlService: new AccessControlService(dataHubClient),
    }),
    [dataHubClient, workspaceClient],
  );
  const mutation = useMutation({
    mutationFn: () =>
      convertWorkspaceToPrivate(
        {
          workspace,
          callerIdentifiers,
          roles: {
            owner: ownerGroups.map((group) => group.id),
            contributor: contributorGroups.map((group) => group.id),
            reader: readerGroups.map((group) => group.id),
          },
        },
        dependencies,
      ),
    onSuccess: ({ workspace: converted }) => onConverted(converted),
  });
  const hasNonOwnerAssignments =
    contributorGroups.length > 0 || readerGroups.length > 0;
  const needsOwnerGroup = ownerGroups.length === 0 && hasNonOwnerAssignments;

  return (
    <>
      <Text>
        Conversion is permanent. Access will be enforced through a new
        restricted Data Lake dataset. Groups are optional during conversion.
        Until access is configured, only the creator can use the workspace.
      </Text>
      <WorkspaceRoleEditor
        label="Owners"
        description="Can manage workspace settings and access."
        groups={ownerGroups}
        onChange={setOwnerGroups}
        disabled={mutation.isPending}
      />
      {needsOwnerGroup && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Select an Owner group before assigning Contributors or Readers.
          </MessageBarBody>
        </MessageBar>
      )}
      <WorkspaceRoleEditor
        label="Contributors"
        description="Can create and update workspace resources."
        groups={contributorGroups}
        onChange={setContributorGroups}
        disabled={mutation.isPending}
      />
      <WorkspaceRoleEditor
        label="Readers"
        description="Can view workspace resources."
        groups={readerGroups}
        onChange={setReaderGroups}
        disabled={mutation.isPending}
      />
      {mutation.error !== null && (
        <MessageBar intent="error">
          <MessageBarBody>
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Workspace conversion failed.'}
          </MessageBarBody>
        </MessageBar>
      )}
      <Button
        appearance="primary"
        disabled={!isCreator || needsOwnerGroup || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? 'Converting workspace...' : 'Convert to private'}
      </Button>
    </>
  );
}

export function PrivateWorkspaceConversionSection(
  props: PrivateWorkspaceConversionSectionProps,
) {
  return (
    <DataHubClientProvider>
      <PrivateWorkspaceConversionFields {...props} />
    </DataHubClientProvider>
  );
}
